import { ArrowDownTrayIcon, BarsArrowDownIcon, BarsArrowUpIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { itemCountPerPage } from 'config'
import { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { downloadFile } from 'services'
import { downloadUtmAnalysis, getUtmAnalysis } from 'services/apis/utms'
import { Pagination } from 'stories/components'
import { InputConvert, InputValidate } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { defaultInputs } from './constant'
import type { UtmAnalysis } from './types'

export function ManageUtmAnalysis() {
  const navigate = useHistory()
  const [isLoading, setLoading] = useState(false)
  const [total, setTotal] = useState(0)
  const [pageNum, setPageNum] = useState(0)
  const [utmLogs, setUtmLogs] = useState<UtmAnalysis[]>([])
  const [values, setValues] = useState<Record<string, any>>({})
  const [filters, setFilters] = useState<Record<string, any>>({
    query: '',
    source: '',
    campaign: '',
    orderBy: 'createdAt',
    orderDir: '-1',
  })

  const [inputs, setInputs] = useState(defaultInputs())
  const [debounceTimeout, setDebounceTimeout] = useState<NodeJS.Timeout | null>(null)
  const [totalWebsitePerClick, setTotalWebsitePerClick] = useState(0)
  const [totalNewpaClick, setTotalNewpaClick] = useState(0)
  const isInitialMount = useRef(true)

  useEffect(() => {
    if (isLoading) return

    filterData(filters)
  }, [pageNum])

  useEffect(() => {
    // Skip the debounced filter on initial mount to avoid duplicate API call
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    if (debounceTimeout) clearTimeout(debounceTimeout)
    const timeout = setTimeout(() => {
      if (!isLoading) filterData(filters, 0)
      setPageNum(0)
    }, 700)
    setDebounceTimeout(timeout)
    return () => clearTimeout(timeout)
  }, [filters.query, filters.source, filters.campaign])

  const onChangeInput = (key: string, value: string) => {
    let newValues = cloneDeep(values)
    let newInputs = cloneDeep(inputs)
    value = InputConvert(inputs[key], value)

    newValues[key] = value
    newInputs[key].error = InputValidate({ ...newInputs[key], value })
    setInputs(newInputs)
    setValues(newValues)

    if (['query', 'source', 'campaign'].includes(key)) {
      setFilters((prev) => ({ ...prev, [key]: value }))
    }
  }

  const filterData = async (filters: any, _pageNum: number = -1) => {
    if (isLoading) return
    if (_pageNum === -1) _pageNum = pageNum
    const filterData = {
      ...filters,
      skip: pageNum * itemCountPerPage,
      count: itemCountPerPage,
    }

    setLoading(true)
    const { data, total, totalNewpaClick, totalWebsitePerClick } = await getUtmAnalysis(filterData)
    setUtmLogs(data)
    setTotal(total)
    setTotalWebsitePerClick(totalWebsitePerClick || 0)
    setTotalNewpaClick(totalNewpaClick || 0)
    setLoading(false)
  }

  const onPageNavigate = (num: number) => {
    setPageNum(num)
  }

  const onDownload = async () => {
    const res: Blob = await downloadUtmAnalysis()

    downloadFile('utmanalysis.xlsx', res)
  }

  const onKeywordClick = (keyword: string) => {
    navigate.push(`/admin_tool/utm?tab=logs&keyword=${encodeURIComponent(keyword ? keyword : 'email campaign')}`)
  }

  const renderHeader = (title: string | JSX.Element, sortable: boolean = false, key: string, total?: number) => {
    let sortOrder = filters.orderBy == key ? parseInt(filters.orderDir) : 0
    if (!sortable)
      return (
        <th scope="col" className="py-3 px-2" key={key}>
          <div className="flex flex-row items-center gap-3">
            <span>{title}</span>
            {typeof total === 'number' && <span className="text-shade-blue font-bold text-base">{total}</span>}
          </div>
        </th>
      )

    const onSort = () => {
      if (sortOrder == 0) sortOrder = -1
      const newFilters = Object.assign({}, filters)
      newFilters['orderBy'] = key
      newFilters['orderDir'] = `${0 - sortOrder}`
      setFilters(newFilters)
      filterData(newFilters)
    }

    return (
      <th scope="col" className="py-2 px-2 min-w-[180px]" key={key}>
        <button
          className="flex flex-row items-center gap-3 uppercase bg-transparent font-bold"
          onClick={() => onSort()}
        >
          <span>{title}</span>
          {typeof total === 'number' && <span className="text-shade-blue font-bold text-base">{total}</span>}
          {sortOrder !== 0 ? (
            sortOrder == 1 ? (
              <BarsArrowUpIcon className="w-3 h-3 ml-1 text-gray-900" />
            ) : (
              <BarsArrowDownIcon className="w-3 h-3 ml-1 text-gray-900" />
            )
          ) : (
            <div className="w-3 h-3 ml-1" />
          )}
        </button>
      </th>
    )
  }

  return (
    <div>
      <div className="relative">
        <div className="flex flex-wrap justify-between">
          <h1 className="text-2xl font-variation-settings-600 flex items-center mb-3">
            <span>UTM Analysis</span>
          </h1>
        </div>
        <div className="flex flex-row justify-between gap-6 md:gap-14 lg:gap-24 mb-3">
          <div className="grid gap-4 md:grid-cols-3 grid-cols-1 w-full">
            {Object.keys(inputs).map((key, index) => {
              let input = inputs[key]
              input.value = values[key]
              return (
                <div className={`input md:col-span-${input.span || 1}`} key={index}>
                  <RenderInput input={input} Key={key} onChange={onChangeInput} />
                </div>
              )
            })}
          </div>
          <div className="flex items-end p-1 text-sm cursor-pointer text-shade-blue rounded" onClick={onDownload}>
            <ArrowDownTrayIcon className="w-6 h-6 p-1 hover-shadow1" />
          </div>
        </div>

        <div className="relative overflow-auto shadow-md sm:rounded-lg">
          <LayoutLoading show={isLoading} />
          <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
            <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200 dark:bg-gray-700 dark:text-gray-400">
              <tr>
                <th scope="col" className="px-2 py-3 w-[40px]">
                  No
                </th>
                {renderHeader('Keyword', true, 'keyword')}
                {renderHeader('Website Clicks', true, 'websiteperclick', totalWebsitePerClick)}
                {renderHeader('Form Submit', true, 'newpaclick', totalNewpaClick)}
              </tr>
            </thead>
            <tbody className="text-[14px] text-gray-900">
              {utmLogs.map((value, index) => {
                return (
                  <tr key={index} className={`border-b ${index % 2 && 'bg-slate-50'}`}>
                    <td className="font-variation-settings-600 text-shade-blue hover:underline cursor-pointer pl-3 py-3">
                      {index + 1 + pageNum * itemCountPerPage}
                    </td>

                    <td className="px-2 py-2">
                      <button
                        className="text-shade-blue hover:underline cursor-pointer bg-transparent p-0 font-inherit text-left"
                        onClick={() => onKeywordClick(value.keyword)}
                      >
                        {value.keyword ? value.keyword : 'email campaign'}
                      </button>
                    </td>

                    <td className="px-2 py-2">{value.websiteperclick}</td>

                    <td className="px-2 py-2">{value.newpaclick}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        <div className="flex justify-end items-center mt-3">
          <div className="flex-1" />
          <Pagination
            totalCount={total}
            itemCountPerPage={itemCountPerPage}
            onNavigate={onPageNavigate}
            pageNum={pageNum}
          />
        </div>
      </div>
    </div>
  )
}
